import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { resolvePath, pathsFor } from '../config/routeSlugs';

const BASE_URL = 'https://poollex.io';

const SEO = ({ 
  title, 
  description, 
  keywords = [],
  path = '',
  image = '/poollex-og-image.jpg',
  type = 'website',
  noindex = false,
  structuredData = null
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'es';
  
  // Sprint 119.71 — Canonical amb subcarpeta idioma correcta.
  // Abans: canonical apuntava sempre a `path` neutre (ex. /initiative)
  // independentment de l'idioma actiu. Amb URLs velles ?lng=fr, ?lng=de,
  // etc. indexades a Google, resultava en múltiples URLs apuntant al
  // mateix canonical neutre → GSC reportava "Duplicada: sin canonical".
  //
  // Ara: canonical inclou l'idioma actiu (/es/initiative, /fr/initiative...)
  // per les 7 pàgines clau on tenim hreflang. Per la resta, canonical
  // neutre (com abans) — no volem fragmentar SEO en pàgines secundàries.
  // Sprint 138 — /calculadora-sal-piscina entra aquí perquè el canonical
  // ha d'apuntar a la versió de l'idioma actiu. Si seguís apuntant a la
  // ruta ES neutra, li estaríem dient a Google que la bona és la
  // castellana i les versions per idioma no s'indexarien mai — que és
  // justament el que volem canviar.
  // Constants al modul: dins del JSX de Helmet no hi pot haver una funcio
  // que les declari, que es el que amagava el bug.
  const HREFLANG_LANGS = ['es', 'en', 'fr', 'de', 'it', 'nl'];
  // Sprint 154 — La calculadora de sal surt d'aquesta llista: les seccions
  // amb slug per idioma es resolen des de routeSlugs.js, on la traduccio
  // d'una ruta no es posar-hi un prefix sino canviar el slug sencer.
  const HREFLANG_PATHS = ['/', '/ficha-tecnica', '/initiative', '/instaladores', '/solicitar-sello', '/pool-check', '/calculadoras'];
  const cleanPath = path.replace(/^\/(es|en|fr|de|it|nl)(?=\/|$)/, '') || '/';
  // Les pagines passen el `path` del seu idioma canonic (p.ex. sempre
  // '/calculadora-sal-piscina'); aqui es resol la seccio i s'emet la URL de
  // l'idioma ACTIU.
  const slugHit = resolvePath(path) || resolvePath(cleanPath);
  const slugPaths = slugHit ? pathsFor(slugHit.key) : null;
  const usesLangSubfolder = !slugHit && HREFLANG_PATHS.includes(cleanPath);
  // Sprint 138 — El castellà NO porta prefix: /es/<ruta> respon 301 cap a
  // /<ruta> (Sprint 119.119c). Declarar-lo com a canonical era apuntar a
  // una redirecció i contradir el que el servidor ja injecta a Googlebot,
  // que és la ruta demanada. Ara les dues bandes diuen el mateix.
  const canonicalPath = slugPaths
    ? (slugPaths[currentLang] || slugPaths[Object.keys(slugPaths)[0]])
    : usesLangSubfolder
      ? (currentLang === 'es'
          ? cleanPath
          : (cleanPath === '/' ? `/${currentLang}` : `/${currentLang}${cleanPath}`))
      : path;
  const fullUrl = `${BASE_URL}${canonicalPath}`;
  const fullImageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  // Sprint 119.17 — Amb la reorientació a certificació ambiental EN 17645
  // (norma europea), obrim FR i IT al selector. La norma cobreix tota la
  // UE i el marc regulador és comú. Anterior: ES+EN+DE (Sprint 107h/j).
  // Sprint 119.97 — NL 6è idioma (GSC 2026-08-12: 21 imp/setmana, pos 5.86).
  const langMap = {
    es: 'es-ES',
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    it: 'it-IT',
    nl: 'nl-NL',
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLang} />
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Sprint 119.39 — hreflang re-activat amb subcarpetes /es/ /fr/ /de/
          /it/ /en/ com a URLs canòniques per idioma. Només s'emeten per
          les 7 pàgines clau (home, ficha-tecnica, initiative, instaladores,
          solicitar-sello, pool-check, calculadoras). Per la resta el
          site segueix funcionant amb URLs neutrals + i18next client. */}
      {/* Sprint 151 — Això NO emetia res, i no ho feia des que es va escriure.
          Anava dins d'una funció que retornava un fragment, i react-helmet no
          recorre els fills d'un fragment niat: els descarta sense dir res. El
          canonical per idioma d'aquest mateix component sí que funcionava, o
          sigui que la condició era bona i el bug passava desapercebut.
          Resultat: les 8 pàgines clau no han anunciat MAI les seves versions
          per idioma a la pròpia pàgina. Al sitemap sí que hi eren (ef289eb).
          Ara els <link> van com a fills directes de Helmet, que és l'única
          forma que recorre.

          El castellà no porta prefix: /es/<ruta> fa 301 cap a /<ruta>, i un
          hreflang que apunta a una redirecció Google l'ignora — i en
          ignorar-lo se li trenca el conjunt sencer, no només la variant
          espanyola. */}
      {/* Sprint 154 — Seccions amb slug per idioma: cada llengua te la seva
          URL propia, no la mateixa amb prefix. */}
      {/* Sprint 159 — Una sola regla a les tres fonts (server.js, aquest
          component i el sitemap): codi SENSE regio i x-default = la versio
          anglesa, o la castellana si no n'hi ha. Fins ara aqui es deia
          x-default = castella i es-ES/en-US, mentre el servidor injectava
          x-default = angles a la mateixa pagina: dos x-default diferents al
          <head> renderitzat de la portada i de cinc calculadores. */}
      {slugPaths && Object.entries(slugPaths).map(([lg, p2]) => (
        <link key={`s-${lg}`} rel="alternate" hrefLang={lg} href={`${BASE_URL}${p2}`} />
      ))}
      {slugPaths && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${BASE_URL}${slugPaths.en || slugPaths.es || Object.values(slugPaths)[0]}`}
        />
      )}
      {usesLangSubfolder && HREFLANG_LANGS.map((lg) => (
        <link
          key={lg}
          rel="alternate"
          hrefLang={lg}
          href={`${BASE_URL}${lg === 'es'
            ? (cleanPath === '/' ? '/' : cleanPath)
            : (cleanPath === '/' ? `/${lg}` : `/${lg}${cleanPath}`)}`}
        />
      ))}
      {usesLangSubfolder && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${BASE_URL}${cleanPath === '/' ? '/en' : `/en${cleanPath}`}`}
        />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="PoolLex.io" />
      <meta property="og:locale" content={langMap[currentLang] || 'es_ES'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-defined SEO configs for each page
export const seoConfigs = {
  home: {
    es: {
      title: 'Documentación técnica de tu piscina, cuando te la pidan',
      description: 'La ficha técnica, el proyecto y el informe que te piden para tu piscina: ayuntamiento, compraventa o reforma. Empieza gratis, sin cuenta.',
      keywords: ['documentacion tecnica piscina', 'ficha tecnica piscina', 'legalizar piscina', 'proyecto tecnico piscina', 'piscina ayuntamiento documentacion', 'certificado piscina compraventa', 'piscina obra menor', 'requerimiento urbanistico piscina', 'ingeniero piscina', 'PoolLex']
    },
    en: {
      title: "Technical documentation for your pool, when it's asked for",
      description: "The technical record, the project and the report you're asked for: the council, a sale, a refurbishment. Free to start, no account.",
      keywords: ['pool technical documentation', 'pool technical record', 'pool permit documents', 'swimming pool project', 'pool compliance documents', 'pool sale certificate', 'pool engineer report', 'PoolLex']
    },
    fr: {
      title: 'Documentation technique de piscine, quand on vous la demande',
      description: "La fiche technique, le projet et le rapport qu'on vous demande pour votre piscine : mairie, vente, rénovation. Gratuit pour commencer.",
      keywords: ['documentation technique piscine', 'fiche technique piscine', 'declaration piscine mairie', 'projet technique piscine', 'piscine vente certificat', 'PoolLex']
    },
    de: {
      title: 'Technische Pool-Dokumentation, wenn sie verlangt wird',
      description: 'Das technische Datenblatt, das Projekt und der Bericht, die verlangt werden: Gemeinde, Verkauf, Sanierung. Kostenloser Einstieg.',
      keywords: ['technische Dokumentation Pool', 'Pool Datenblatt', 'Pool Genehmigung Unterlagen', 'Schwimmbad Projekt', 'Pool Verkauf Nachweis', 'PoolLex']
    },
    it: {
      title: 'Documentazione tecnica della piscina, quando te la chiedono',
      description: 'La scheda tecnica, il progetto e la relazione che ti chiedono per la piscina: comune, compravendita, ristrutturazione. Gratis per iniziare.',
      keywords: ['documentazione tecnica piscina', 'scheda tecnica piscina', 'piscina pratica comune', 'progetto tecnico piscina', 'piscina vendita certificato', 'PoolLex']
    },
    nl: {
      title: 'Technische documentatie zwembad, als erom gevraagd wordt',
      description: 'De technische fiche, het project en het rapport die gevraagd worden: gemeente, verkoop, verbouwing. Gratis om te beginnen.',
      keywords: ['technische documentatie zwembad', 'technische fiche zwembad', 'zwembad vergunning documenten', 'zwembad project', 'zwembad verkoop certificaat', 'PoolLex']
    }
  },
  passport: {
    es: {
      title: 'Ficha Técnica Piscina GRATIS · 2 min · PoolLex ID Europeo',
      description: 'Genera la Ficha Técnica de tu piscina con su PoolLex ID: dimensiones, volumen y referencia catastral. Gratis en 2 minutos, sin crear cuenta.',
      keywords: ['generar PoolLex ID piscina', 'ficha técnica piscina gratis', 'calcular volumen piscina', 'PDF piscina', 'QR piscina']
    },
    en: {
      title: 'Pool Technical Sheet Generator · Free, 2 min · PoolLex ID',
      description: "Generate your pool's Technical Sheet with unique European PoolLex ID. Dimensions, volume, cadastral reference. Free in 2 minutes, no account required.",
      keywords: ['generate pool identification technical sheet', 'free pool technical sheet', 'calculate pool volume', 'pool PDF', 'pool QR code']
    },
    fr: {
      title: 'Fiche Technique Piscine GRATUIT · 2 min · PoolLex ID',
      description: 'Générez la Fiche Technique de votre piscine avec PoolLex ID unique européen. Dimensions, volume, référence cadastrale. Gratuit en 2 minutes.',
      keywords: ['générer fiche technique d\'identification piscine', 'fiche technique piscine gratuit', 'calculer volume piscine', 'PDF piscine']
    },
    de: {
      title: 'Pool Datenblatt Generator · Gratis 2 Min · PoolLex ID',
      description: 'Erstellen Sie das technische Datenblatt Ihres Pools mit eindeutiger europäischer PoolLex ID. Abmessungen, Volumen, Katasterreferenz. Kostenlos in 2 Min.',
      keywords: ['Identifikations-Datenblatt Pool erstellen', 'technisches Datenblatt Pool kostenlos', 'Poolvolumen berechnen', 'Pool PDF']
    },
    it: {
      title: 'Scheda Tecnica Piscina GRATIS · 2 min · PoolLex ID Europeo',
      description: 'Genera la Scheda Tecnica della tua piscina con il suo PoolLex ID: dimensioni, volume e riferimento catastale. Gratis in 2 minuti.',
      keywords: ['generare scheda tecnica di identificazione piscina', 'scheda tecnica piscina gratis', 'calcolare volume piscina', 'PDF piscina']
    },
    nl: {
      title: 'Technische Fiche Zwembad GRATIS · 2 min · PoolLex ID',
      description: 'Genereer gratis de technische fiche van je zwembad met een unieke Europese PoolLex ID. Afmetingen en volume in 2 minuten.',
      keywords: ['technische fiche zwembad', 'zwembad documenteren', 'PoolLex ID', 'zwembad identificatie', 'zwembadpaspoort', 'zwembadgegevens', 'zwembad registreren', 'zwembaddocumentatie', 'gratis zwembadfiche']
    }
  },
  check: {
    es: {
      title: 'Pool Check 2026: Verifica Piscina en 2 min · GRATIS',
      description: 'Verifica en 2 minutos si tu piscina cumple con la normativa: urbanística, catastral y sanitaria. Test gratuito basado en tu municipio. Sin crear cuenta.',
      keywords: ['verificar piscina', 'legalizar piscina', 'conformidad piscina', 'regularizar piscina', 'test piscina']
    },
    en: {
      title: 'Pool Check 2026: Verify Your Pool in 2 min · FREE',
      description: "Check whether your pool is administratively in order. Free analysis based on location, age and features. Currently covers Spain only.",
      keywords: ['verify pool', 'legalize pool', 'pool compliance', 'regularize pool', 'pool test']
    },
    fr: {
      title: 'Pool Check 2026 : Vérifiez votre Piscine en 2 min · GRATUIT',
      description: 'Vérifiez si votre piscine est en règle sur le plan administratif. Analyse gratuite selon la localisation, l\'âge et les équipements. Espagne uniquement.',
      keywords: ['vérifier piscine', 'légaliser piscine', 'conformité piscine', 'régulariser piscine']
    },
    de: {
      title: 'Pool Check 2026: Pool in 2 Min prüfen · KOSTENLOS',
      description: 'Prüfen Sie, ob Ihr Pool verwaltungstechnisch in Ordnung ist. Kostenlose Analyse nach Lage, Alter und Ausstattung. Derzeit nur für Spanien.',
      keywords: ['Pool prüfen', 'Pool legalisieren', 'Pool-Konformität', 'Pool regularisieren']
    },
    it: {
      title: 'Pool Check 2026: Verifica Piscina in 2 min · GRATIS',
      description: 'Verifica se la tua piscina è in regola sul piano amministrativo. Analisi gratuita in base a posizione, età e dotazioni. Per ora solo Spagna.',
      keywords: ['verificare piscina', 'legalizzare piscina', 'conformità piscina', 'regolarizzare piscina']
    },
    nl: {
      title: 'Pool Check 2026: Controleer je Zwembad in 2 min · GRATIS',
      description: 'Controleer of je zwembad administratief in orde is. Gratis analyse op basis van locatie, ouderdom en kenmerken. Momenteel gericht op Spanje.',
      keywords: ['zwembad legaliseren', 'zwembad vergunning', 'zwembad kadaster', 'zwembad regulariseren', 'is mijn zwembad legaal', 'zwembad zonder vergunning', 'pool check', 'zwembadcontrole']
    }
  },
  // Sprint 93 — Calculadora de càrrega estructural per a terrasses.
  // Inclou keywords concrets per a la consulta "puc posar piscina
  // hinchable a la terrassa" + variants CTE/normativa.
  pool_base: {
    es: {
      // 57 car. Promet el que la pagina dona: croquis i mediciones, no
      // un calcul estructural. El «sin registro» esta verificat: aquesta
      // calculadora no te cap mur de pagament ni cap formulari.
      title: 'Base para piscina elevada: croquis y mediciones',
      description: 'Qué base necesita tu piscina elevada o desmontable según el terreno y el desnivel: croquis acotado, m³ de zahorra u hormigón y tierras a retirar.',
      keywords: [
        'base piscina desmontable', 'solera piscina elevada',
        'como nivelar terreno piscina', 'losa hormigon piscina desmontable',
        'zahorra base piscina', 'preparar suelo piscina desmontable',
        'arena base piscina', 'piscina desmontable cesped',
      ],
    },
  },
  // 21/10/2026 — Configurador de cubiertas. 52 car. de títol.
  cover_config: {
    es: {
      title: 'Configurador de cubiertas de piscina en 3D',
      // Sense preus des del 22/10: la description NO pot prometre un preu
      // que la pàgina ja no dona (veure l'auditoria de descriptions).
      description: 'Configura la cubierta de tu piscina y mírala en 3D, abierta y cerrada. Comprueba que cabe en tu playa y descarga los planos para el ayuntamiento.',
      keywords: [
        'cubierta piscina', 'cubierta telescópica piscina', 'planos cubierta piscina',
        'configurador cubierta piscina', 'cubierta piscina baja', 'cubierta piscina alta',
        'abrigo piscina', 'cerramiento piscina',
      ],
    },
  },
  terrace_load: {
    es: {
      title: '¿Aguanta tu terraza una piscina? Cálculo según CTE',
      description: '¿Soporta el forjado una piscina desmontable llena? El límite del CTE depende de la categoría de tu terraza, y se supera antes de lo que parece.',
      keywords: [
        'piscina hinchable terraza', 'piscina desmontable terraza',
        'cargas CTE terraza', 'peso piscina terraza', 'CTE DB-SE-AE',
        'soportará terraza piscina', 'piscina balcón', 'qk terraza piscina',
        'calculadora carga estructural piscina',
      ],
    },
    en: {
      title: 'Will Your Terrace Hold a Pool? CTE Load Calc · FREE 2026',
      description: 'Free calculator: check if your terrace holds a filled pool under CTE DB-SE-AE. The limit depends on your terrace category, and it is lower than it looks.',
      keywords: [
        'above-ground pool terrace', 'inflatable pool balcony',
        'pool weight terrace', 'terrace load calculator', 'CTE Spain pool',
        'structural load above-ground pool',
      ],
    },
    fr: {
      title: 'Terrasse et Piscine Hors-Sol : Calc Charge · GRATUIT 2026',
      description: 'Calculez gratuitement si votre terrasse supporte une piscine remplie selon CTE DB-SE-AE. La limite dépend de la catégorie de votre terrasse.',
      keywords: [
        'piscine hors-sol terrasse', 'piscine gonflable balcon',
        'poids piscine terrasse', 'calculateur charge terrasse',
        'CTE Espagne piscine',
      ],
    },
    de: {
      title: 'Terrasse Pool-Tragfähigkeit Rechner · GRATIS 2026',
      description: 'Kostenloser Rechner: Trägt Ihre Terrasse einen gefüllten Pool nach CTE DB-SE-AE? Die Grenze hängt von der Kategorie Ihrer Terrasse ab.',
      keywords: [
        'Aufstellpool Terrasse', 'aufblasbarer Pool Balkon',
        'Pool Gewicht Terrasse', 'Terrassen-Lastrechner',
        'CTE Spanien Pool',
      ],
    },
    it: {
      title: 'La tua Terrazza Regge una Piscina? Calc CTE · GRATIS 2026',
      description: 'Calcola gratis se la tua terrazza regge una piscina piena secondo CTE DB-SE-AE. Il limite dipende dalla categoria della terrazza.',
      keywords: [
        'piscina fuori terra terrazza', 'piscina gonfiabile balcone',
        'peso piscina terrazza', 'calcolatore carico terrazza',
        'CTE Spagna piscina',
      ],
    },
    nl: {
      title: 'Draagt je Terras een Zwembad? Berekening CTE · GRATIS 2026',
      description: 'Bereken of je terrasvloer het gewicht van een gevuld zwembad draagt volgens de CTE. De grens hangt af van de categorie van je terras.',
      keywords: ['draagkracht terras zwembad', 'gewicht opzetzwembad terras', 'mag een zwembad op mijn terras', 'belasting terrasvloer', 'opzetzwembad op balkon', 'kg per m2 terras']
    }
  },

  // ── Calculadores amb slug castellà ─────────────────────────────
  // Sprint 141. Fins ara el títol el deia el servidor (OG_ROUTES) i el
  // tornava a dir React des de l'i18n, amb text DIFERENT. Mentre només
  // els bots rebien la injecció era invisible; des que s'injecta per a
  // tothom, l'usuari veuria el títol canviar-li davant.
  //
  // El text base és el castellà del sprint del 27/08 (llenguatge natural
  // amb un fet tècnic útil), traduït mantenint aquest to. NO s'ha
  // reaprofitat l'estil vell de l'i18n («2026 · GRATIS»).
  //
  // Les tres de /tools/ (volum, energia, aigua) es queden fora a propòsit:
  // tenen metadades en anglès deliberades i 1.443 impressions
  // posicionant per consultes angleses. Decisió ajornada.

  // Calculadora de clor. El text estava CODIFICAT dins el component i no
  // existia a cap altre idioma. Portava, a més, una errada: deia
  // «cloque» on havia de dir «choque».
  chlorine: {
    es: {
      title: 'Calculadora de cloro para piscina: dosis en gramos',
      description: 'Calcula la dosis de cloro según volumen y cloro libre actual. El pH condiciona su eficacia: por encima de 7,8 pierde buena parte del poder desinfectante.',
      keywords: ['calculadora cloro piscina', 'dosis cloro piscina', 'cuanto cloro echar piscina', 'cloro libre ppm']
    },
    en: {
      title: 'Pool chlorine calculator: the dose in grams',
      description: 'Work out the chlorine dose from your pool volume and current free chlorine. pH drives how well it works: above 7.8 it loses much of its disinfecting power.',
      keywords: ['pool chlorine calculator', 'chlorine dose pool', 'how much chlorine pool', 'free chlorine ppm']
    },
    fr: {
      title: 'Calcul du chlore pour piscine : dose en grammes',
      description: 'Calculez la dose de chlore selon le volume et le chlore libre actuel. Au-delà de pH 7,8, il perd une grande partie de son pouvoir désinfectant.',
      keywords: ['calcul chlore piscine', 'dose chlore piscine', 'combien de chlore piscine']
    },
    de: {
      title: 'Chlor-Rechner für den Pool: Dosis in Gramm',
      description: 'Berechnen Sie die Chlordosis aus Poolvolumen und aktuellem freien Chlor. Über pH 7,8 verliert Chlor viel Desinfektionskraft.',
      keywords: ['chlor rechner pool', 'chlordosis pool', 'wie viel chlor pool']
    },
    it: {
      title: 'Calcolo del cloro in piscina: dose in grammi',
      description: "Calcola la dose di cloro in base al volume e al cloro libero attuale. Il pH ne condiziona l'efficacia: oltre 7,8 perde gran parte del potere disinfettante.",
      keywords: ['calcolo cloro piscina', 'dose cloro piscina', 'quanto cloro piscina']
    },
    nl: {
      title: 'Chloor berekenen voor je zwembad: dosis in gram',
      description: 'Bereken de chloordosis op basis van het volume en het huidige vrije chloor. Boven pH 7,8 verliest chloor veel desinfecterende kracht.',
      keywords: ['chloor berekenen zwembad', 'chloordosis zwembad', 'hoeveel chloor zwembad']
    },
  },

  // Temps de filtració.
  filtration: {
    es: {
      title: 'Calculadora de filtración: simulador del filtro de arena',
      description: 'Calcula las horas de filtración de tu piscina y mira en el simulador cómo trabajan el filtro de arena y la válvula selectora, contralavado incluido.',
      keywords: ['tiempo filtracion piscina', 'horas depuradora piscina', 'caudal bomba piscina']
    },
    en: {
      title: 'Pool filtration calculator and sand filter simulator',
      description: "Calculate your pool's filtration hours and see in the simulator how the sand filter and multiport valve work, backwash included.",
      keywords: ['pool filtration time', 'pool pump hours per day', 'pool turnover rate']
    },
    fr: {
      title: 'Calculateur de filtration : simulateur du filtre à sable',
      description: 'Calculez les heures de filtration de votre piscine et voyez dans le simulateur comment fonctionnent le filtre à sable et la vanne, contre-lavage compris.',
      keywords: ['temps filtration piscine', 'heures filtration piscine', 'debit pompe piscine']
    },
    de: {
      title: 'Filtrationsrechner: Simulator für den Sandfilter',
      description: 'Berechnen Sie die Filterstunden Ihres Pools und sehen Sie im Simulator, wie Sandfilter und Mehrwegeventil arbeiten, inklusive Rückspülung.',
      keywords: ['filterzeit pool', 'pumpe laufzeit pool', 'umwaelzung pool']
    },
    it: {
      title: 'Calcolatore di filtrazione: simulatore del filtro a sabbia',
      description: 'Calcola le ore di filtrazione della piscina e guarda nel simulatore come lavorano il filtro a sabbia e la valvola, controlavaggio compreso.',
      keywords: ['tempo filtrazione piscina', 'ore pompa piscina', 'portata pompa piscina']
    },
    nl: {
      title: 'Filtratie-rekenmodule en zandfilter-simulator',
      description: 'Bereken de filtratie-uren van uw zwembad en zie in de simulator hoe het zandfilter en de multiklep werken, terugspoelen inbegrepen.',
      keywords: ['filtratietijd zwembad', 'pomp uren zwembad', 'debiet pomp zwembad']
    },
  },

  // Bomba de calor.
  heatpump: {
    es: {
      title: 'Bomba de calor para piscina: cálculo de potencia',
      description: 'Calcula los kW necesarios según volumen y salto térmico. Sin cubierta, buena parte del calor aportado se pierde por evaporación cada noche.',
      keywords: ['bomba calor piscina', 'potencia bomba calor piscina', 'kw calentar piscina']
    },
    en: {
      title: 'Pool heat pump: work out the power you need',
      description: 'Calculate the kW you need from your pool volume and temperature rise. Without a cover, much of the heat you add is lost to evaporation every night.',
      keywords: ['pool heat pump calculator', 'heat pump size pool', 'kw to heat a pool']
    },
    fr: {
      title: 'Pompe à chaleur piscine : calcul de la puissance',
      description: "Calculez les kW nécessaires selon le volume et l'écart de température. Sans couverture, une grande part de la chaleur apportée s'évapore chaque nuit.",
      keywords: ['pompe a chaleur piscine', 'puissance pac piscine', 'chauffer piscine kw']
    },
    de: {
      title: 'Pool-Wärmepumpe: die nötige Leistung berechnen',
      description: 'Berechnen Sie die kW aus Poolvolumen und gewünschter Temperaturerhöhung. Ohne Abdeckung geht ein Großteil der Wärme jede Nacht durch Verdunstung verloren.',
      keywords: ['waermepumpe pool', 'leistung waermepumpe pool', 'pool heizen kw']
    },
    it: {
      title: 'Pompa di calore piscina: calcolo della potenza',
      description: 'Calcola i kW necessari in base al volume e al salto termico. Senza copertura, gran parte del calore immesso si perde ogni notte per evaporazione.',
      keywords: ['pompa di calore piscina', 'potenza pompa calore piscina', 'riscaldare piscina kw']
    },
    nl: {
      title: 'Warmtepomp zwembad: het benodigde vermogen',
      description: 'Bereken het vermogen in kW uit het volume en de gewenste temperatuurstijging. Zonder afdekking gaat veel warmte elke nacht verloren door verdamping.',
      keywords: ['warmtepomp zwembad', 'vermogen warmtepomp zwembad', 'zwembad verwarmen kw']
    },
  },

  // Plaques solars.
  solar: {
    es: {
      title: 'Placas solares para piscina: cálculo de ahorro',
      description: 'Calcula el ahorro de la solar en tu piscina. Antes de dimensionar: la cubierta evita la mayor parte de las pérdidas térmicas por mucho menos.',
      keywords: ['placas solares piscina', 'solar piscina ahorro', 'autoconsumo piscina']
    },
    en: {
      title: 'Solar panels for a pool: how much you save',
      description: 'Calculate what solar saves on your pool. Before sizing anything: a cover prevents most of the heat loss for a fraction of the cost.',
      keywords: ['solar panels pool', 'pool solar savings', 'solar pool heating']
    },
    fr: {
      title: 'Panneaux solaires piscine : calcul des économies',
      description: "Calculez les économies du solaire sur votre piscine. Avant de dimensionner : la couverture évite l'essentiel des pertes thermiques pour bien moins cher.",
      keywords: ['panneaux solaires piscine', 'solaire piscine economies', 'autoconsommation piscine']
    },
    de: {
      title: 'Solaranlage für den Pool: mögliche Ersparnis',
      description: 'Berechnen Sie, was Solar bei Ihrem Pool spart. Vor der Auslegung: Eine Abdeckung verhindert den Großteil der Wärmeverluste für einen Bruchteil der Kosten.',
      keywords: ['solar pool', 'solaranlage pool ersparnis', 'eigenverbrauch pool']
    },
    it: {
      title: 'Pannelli solari per piscina: calcolo del risparmio',
      description: 'Calcola il risparmio del solare nella tua piscina. Prima di dimensionare: la copertura evita gran parte delle perdite termiche a un costo molto minore.',
      keywords: ['pannelli solari piscina', 'solare piscina risparmio', 'autoconsumo piscina']
    },
    nl: {
      title: 'Zonnepanelen voor je zwembad: wat het bespaart',
      description: 'Bereken wat zonne-energie je zwembad bespaart. Maar eerst: een afdekking voorkomt het grootste deel van het warmteverlies, voor veel minder geld.',
      keywords: ['zonnepanelen zwembad', 'zwembad zonne energie besparing', 'zelfverbruik zwembad']
    },
  },
  // ── Calculadora de sal ──────────────────────────────────────────
  // Sprint 138. GSC 31/08/2026: 229 impressions en neerlandès (posició
  // 17-28, la millor de qualsevol idioma estranger) i 491 en anglès, TOTES
  // amb zero clics. La pàgina servia el títol en castellà a tothom: qui
  // buscava «hoeveel zout in zwembad berekenen» veia un títol en espanyol
  // i no clicava. Les rutes prefixades existeixen des del Sprint 136.
  //
  // Els títols segueixen la formulació real de la cerca a cada idioma, no
  // la traducció literal del castellà: en neerlandès es pregunta «hoeveel
  // zout», en anglès es busca «pool salt calculator».
  // Sprint 152 — La calculadora de VOLUM entra a seoConfigs, pero nomes per
  // als idiomes prefixats.
  //
  // Report GSC 07/09: /de/blog/poolrechner-volumen-berechnen fa 340
  // impressions a posicio 11,9 amb ZERO clics, i es el 94% del volum
  // d'Alemanya. Les 67 consultes alemanyes son totes d'intencio d'EINA
  // ("pool volumen berechnen", "poolinhalt berechnen", "kubikmeter pool
  // berechnen") i cap arriba a l'eina: /de/tools/pool-volume-calculator
  // existeix com a ruta pero s'anuncia en ANGLES, aixi que Google no la puja
  // per consultes alemanyes i qui hi respon es un article de blog.
  //
  // Es el mateix bug que el Sprint 138 va arreglar a la calculadora de sal, i
  // aquella ja dona els primers senyals en neerlandes.
  //
  // `skipEs` protegeix el que ja funciona: la URL sense prefix te metadades
  // en angles a proposit (1.443 impressions posicionant per consultes
  // angleses) i segueix sortint d'OG_ROUTES, intacta. Nomes deixen de mentir
  // les versions per idioma.
  // Sprint 161 — Entra el castellà, amb URL pròpia (/calculadora-volumen-piscina).
  // La URL sense prefix passa a ser anglesa de debò: abans es renderitzava en
  // castellà i per això `skipEs` era necessari.
  volume: {
    es: {
      title: 'Volumen de piscina: litros, m³ y fondo inclinado',
      description: 'Calcula el volumen de tu piscina según su forma. De él dependen todas las dosis de producto y el caudal de la bomba. Gratis, sin registro.',
      keywords: ['calculadora volumen piscina', 'calcular litros piscina', 'metros cubicos piscina', 'cuantos litros tiene mi piscina']
    },
    en: {
      title: 'Pool volume in litres: flat or sloping bottom',
      description: 'Work out your pool volume by shape. Every chemical dose and the required pump flow depends on this number. Free, no signup.',
      keywords: ['pool volume calculator', 'how many litres in a pool', 'pool volume in litres', 'swimming pool volume']
    },
    de: {
      title: 'Poolvolumen in Litern: flacher oder schräger Boden',
      description: 'Berechnen Sie Volumen und Liter Ihres Pools nach Form: rechteckig, rund oder oval. Grundlage für Chlordosierung, Salzmenge und Filterlaufzeit.',
      keywords: ['pool volumen berechnen', 'poolinhalt berechnen', 'kubikmeter pool berechnen', 'wassermenge pool berechnen', 'pool liter berechnen']
    },
    nl: {
      title: 'Zwembad inhoud in liters: vlakke of schuine bodem',
      description: 'Bereken de inhoud van je zwembad per vorm — rechthoekig, rond of ovaal. De basis voor chloordosering, zoutberekening en filtertijd. Gratis, zonder account.',
      keywords: ['zwembad inhoud berekenen', 'hoeveel liter zwembad', 'kubieke meter zwembad', 'zwembad volume berekenen']
    },
    fr: {
      title: 'Volume de piscine en litres : fond plat ou incliné',
      description: 'Calculez le volume de votre piscine selon sa forme — rectangulaire, ronde ou ovale. La base du dosage du chlore, du sel et du temps de filtration. Gratuit.',
      keywords: ['calcul volume piscine', 'combien de litres piscine', 'volume piscine m3', 'calculer volume piscine']
    },
    it: {
      title: 'Volume piscina in litri: fondo piatto o inclinato',
      description: 'Calcola il volume della tua piscina per forma: rettangolare, rotonda o ovale. La base per il dosaggio di cloro e sale e il tempo di filtrazione.',
      keywords: ['calcolo volume piscina', 'quanti litri piscina', 'volume piscina metri cubi', 'calcolare volume piscina']
    }
  },
  // Sprint 152 — energy i water, mateix cas que `volume`: la ruta prefixada
  // existeix pero s'anuncia en angles. Tambe amb skipEs, per no tocar la URL
  // sense prefix, que es la que ja posiciona en angles.
  //
  // La d'aigua es la mes sagnant del report: /tools/pool-water-calculator fa
  // 130 impressions a posicio 4,8 — PAGINA 1 — amb zero clics, i la consulta
  // "how much water does a pool use per day" hi es a posicio 3,1.
  // Sprint 161 — Castellà amb URL pròpia (/calculadora-consumo-electrico-piscina).
  energy: {
    es: {
      title: 'Consumo eléctrico de la piscina: calcula tu coste',
      description: 'Calcula lo que cuesta de verdad tu piscina en marcha. Manda la bomba de filtración: a media velocidad ahorra mucha más potencia de la que alarga las horas.',
      keywords: ['consumo electrico piscina', 'coste electricidad piscina', 'cuanto gasta una piscina', 'consumo depuradora piscina']
    },
    en: {
      title: 'Pool running cost: calculate your electricity use',
      description: 'Calculate what your pool really costs to run. The filtration pump dominates: halving its speed cuts power far more than it extends the hours needed.',
      keywords: ['pool running cost', 'pool electricity cost', 'pool energy calculator', 'how much electricity does a pool use']
    },
    de: {
      title: 'Poolkosten berechnen: Stromverbrauch pro Saison',
      description: 'Berechnen Sie, was Ihr Pool wirklich an Strom kostet. Die Filterpumpe dominiert: halbe Drehzahl spart deutlich mehr Leistung, als sie an Laufzeit kostet.',
      keywords: ['pool stromverbrauch berechnen', 'poolkosten strom', 'was kostet ein pool im monat', 'pool stromkosten']
    },
    nl: {
      title: 'Zwembad stroomverbruik berekenen per seizoen',
      description: 'Bereken wat je zwembad echt aan stroom kost. De filterpomp is bepalend: halve snelheid bespaart veel meer vermogen dan het aan extra draaiuren kost.',
      keywords: ['zwembad stroomverbruik berekenen', 'kosten zwembad per maand', 'zwembad energieverbruik', 'zwembadpomp stroomverbruik']
    },
    fr: {
      title: "Coût de fonctionnement d'une piscine : électricité",
      description: "Calculez ce que votre piscine coûte vraiment en électricité. La pompe de filtration domine : diviser sa vitesse par deux réduit fortement la puissance.",
      keywords: ['coût electricite piscine', 'consommation electrique piscine', 'cout piscine par mois', 'calcul consommation piscine']
    },
    it: {
      title: 'Costo di gestione piscina: consumo elettrico reale',
      description: "Calcola quanto costa davvero far funzionare la tua piscina. La pompa di filtrazione domina: dimezzare la velocità riduce molto la potenza.",
      keywords: ['consumo elettrico piscina', 'costo piscina al mese', 'calcolo consumi piscina', 'costo pompa piscina']
    }
  },
  // Sprint 166 — Castellà amb URL pròpia (/calculadora-consumo-agua-piscina).
  water: {
    es: {
      title: 'Consumo de agua de una piscina: al día y por temporada',
      description: 'Calcula el agua que consume tu piscina al día y por temporada: evaporación, contralavados y reposición. La cubierta reduce la evaporación en torno al 70 %.',
      keywords: ['consumo agua piscina', 'cuanta agua gasta una piscina', 'evaporacion piscina', 'reposicion agua piscina']
    },
    en: {
      title: 'How much water does a pool use? Per day and per year',
      description: 'Work out your pool water use per day and per season: evaporation, backwash and top-up. Evaporation is the biggest share and a cover cuts it by about 70%.',
      keywords: ['how much water does a pool use per day', 'pool water consumption', 'pool evaporation rate', 'pool water usage calculator']
    },
    de: {
      title: 'Wasserverbrauch Pool: pro Tag und pro Saison',
      description: 'Wasserverbrauch Ihres Pools pro Tag und pro Saison: Verdunstung, Rückspülung und Nachfüllen. Eine Abdeckung senkt die Verdunstung um rund 70 %.',
      keywords: ['wasserverbrauch pool', 'pool verdunstung', 'wie viel wasser verbraucht ein pool', 'pool wasserverlust pro tag']
    },
    nl: {
      title: 'Waterverbruik zwembad: per dag en per seizoen',
      description: 'Bereken het waterverbruik van je zwembad per dag en per seizoen: verdamping, terugspoelen en bijvullen. Een afdekking scheelt zo’n 70 %.',
      keywords: ['waterverbruik zwembad', 'zwembad verdamping', 'hoeveel water verbruikt een zwembad', 'zwembad waterverlies per dag']
    },
    fr: {
      title: "Consommation d'eau d'une piscine : par jour et par saison",
      description: "Consommation d'eau de votre piscine par jour et par saison : évaporation, lavage du filtre et appoint. Une couverture la réduit d'environ 70 %.",
      keywords: ["consommation d'eau piscine", 'evaporation piscine', "perte d'eau piscine par jour", 'appoint eau piscine']
    },
    it: {
      title: "Consumo d'acqua della piscina: al giorno e a stagione",
      description: "Consumo d'acqua della piscina al giorno e a stagione: evaporazione, controlavaggio e rabbocco. Una copertura lo riduce di circa il 70 %.",
      keywords: ["consumo acqua piscina", 'evaporazione piscina', 'perdita acqua piscina al giorno', 'quanta acqua consuma una piscina']
    }
  },
  salt: {
    es: {
      title: 'Cuánta sal para tu piscina: kg y sacos de 25 kg',
      description: 'Calcula los kg de sal según volumen y ppm objetivo, con el número de sacos de 25 kg. La sal no se gasta: se diluye con lluvias y contralavados.',
      keywords: ['calculadora sal piscina', 'cuantos kilos sal piscina', 'sal clorador salino', 'ppm sal piscina']
    },
    en: {
      title: 'How much salt for your pool: kg and 25 kg bags',
      description: 'Enter your pool volume and target salinity to get the exact kilos of salt and how many 25 kg bags. Free, no signup, with the engineering behind it.',
      keywords: ['pool salt calculator', 'salt pool calculator', 'pool salt chart', 'how much salt for pool', 'salt water pool ppm']
    },
    nl: {
      title: 'Hoeveel zout voor je zwembad: kg en zakken van 25 kg',
      description: 'Bereken hoeveel kilo zout je zwembad nodig heeft op basis van volume en gewenste ppm. Inclusief het aantal zakken van 25 kg. Gratis.',
      keywords: ['hoeveel zout in zwembad berekenen', 'hoeveel zwembadzout per m3', 'zoutgehalte zwembad ppm', 'hoeveel kg zout in zwembad']
    },
    fr: {
      title: 'Combien de sel pour sa piscine : kg et sacs de 25 kg',
      description: 'Calculez les kilos de sel nécessaires selon le volume de votre piscine et le taux de sel visé. Nombre de sacs de 25 kg inclus. Gratuit, sans inscription.',
      keywords: ['sel piscine dosage', 'calcul sel piscine', 'combien de sel piscine', 'électrolyseur au sel']
    },
    de: {
      title: 'Wie viel Salz für den Pool: kg und 25-kg-Säcke',
      description: 'Berechnen Sie die genaue Salzmenge für Ihren Pool aus Volumen und Ziel-Salzgehalt in ppm. Inklusive Anzahl der 25-kg-Säcke. Kostenlos, ohne Anmeldung.',
      keywords: ['poolsalz rechner', 'salzelektrolyse pool salzgehalt', 'wie viel salz pool', 'salzgehalt pool ppm']
    },
    it: {
      title: 'Quanto sale per la piscina: kg e sacchi da 25 kg',
      description: 'Calcola i chili di sale necessari in base al volume della piscina e al livello di salinità desiderato. Con il numero di sacchi da 25 kg. Gratis.',
      keywords: ['calcolo sale piscina', 'quanto sale piscina', 'elettrolisi sale piscina', 'ppm sale piscina']
    }
  },
  calculators: {
    es: {
      title: 'Calculadoras de piscina: volumen, sal, consumo y coste',
      description: 'Volumen, sal, consumo eléctrico y de agua de tu piscina. Calculadoras con el criterio técnico de ingeniería detrás de cada número.',
      keywords: ['calculadora piscina', 'calcular volumen piscina', 'calculadora sal piscina', 'calculadora cloro', 'tiempo filtración piscina']
    },
    en: {
      title: '8 Free Pool Calculators · Volume, Salt, Energy · 2026',
      description: '8 free calculators for your pool: volume, energy, water, salt, chlorine, filtration, heat pump, solar. No signup, engineering criteria behind every number.',
      keywords: ['pool calculator', 'calculate pool volume', 'pool salt calculator', 'chlorine calculator', 'pool filtration time']
    },
    fr: {
      title: '8 Calculateurs Piscine GRATUITS · Volume, Sel, Énergie',
      description: '8 calculateurs gratuits : volume, énergie, eau, sel, chlore, filtration, pompe à chaleur, solaire. Sans inscription. Par ingénieur diplômé.',
      keywords: ['calculateur piscine', 'calculer volume piscine', 'calculateur sel piscine', 'calculateur chlore']
    },
    de: {
      title: '8 Kostenlose Pool-Rechner · Volumen, Salz, Energie · 2026',
      description: '8 kostenlose Rechner: Volumen, Energie, Wasser, Salz, Chlor, Filterzeit, Wärmepumpe, Solar. Ohne Anmeldung. Von eingetragenem Ingenieur.',
      keywords: ['Pool-Rechner', 'Poolvolumen berechnen', 'Pool-Salzrechner', 'Chlorrechner']
    },
    it: {
      title: '8 Calcolatori Piscina GRATIS · Volume, Sale, Energia · 2026',
      description: '8 calcolatori gratis: volume, energia, acqua, sale, cloro, filtrazione, pompa di calore, solare. Senza registrazione. Da ingegnere iscritto all\'Albo.',
      keywords: ['calcolatore piscina', 'calcolare volume piscina', 'calcolatore sale piscina', 'calcolatore cloro']
    },
    nl: {
      title: '8 Zwembadcalculators GRATIS · Volume, Zout, Energie · 2026',
      description: 'Gratis tools om volume, filtratie, chloor, zout, energie en warmtepomp van je zwembad te berekenen. Door een gediplomeerd ingenieur (ir.).',
      keywords: ['zwembadcalculator', 'zwembad volume berekenen', 'zout berekenen zwembad', 'chloor berekenen zwembad', 'filtratietijd zwembad', 'zwembad stroomverbruik berekenen', 'zwembad waterverbruik berekenen', 'gratis zwembad rekenmodule']
    }
  },
  initiative: {
    es: {
      title: 'PoolLex Initiative · Clasificación Sostenibilidad Piscinas',
      description: 'Iniciativa para democratizar la clasificación ambiental de piscinas según UNE-EN 17645. Plataforma digital abierta, sin coste. Únete al proyecto.',
      keywords: ['iniciativa piscinas', 'documentación piscinas', 'sector piscinas', 'profesionales piscinas', 'colaborar piscinas']
    },
    en: {
      title: 'PoolLex Initiative · Pool Sustainability Classification',
      description: 'Open initiative to democratise pool environmental classification per EN 17645. Digital platform, no cost. Join the project and help scale it.',
      keywords: ['pool initiative', 'pool documentation', 'pool sector', 'pool professionals', 'pool collaboration']
    },
    fr: {
      title: 'PoolLex Initiative · Classification Durabilité Piscines',
      description: "Initiative ouverte pour démocratiser la classification environnementale des piscines selon NF EN 17645. Plateforme numérique, sans coût. Rejoignez-nous.",
      keywords: ['initiative piscines', 'documentation piscines', 'secteur piscines', 'professionnels piscines']
    },
    de: {
      title: 'PoolLex Initiative · Pool-Nachhaltigkeitsklassifizierung',
      description: 'Offene Initiative zur Demokratisierung der Pool-Umweltklassifizierung nach DIN EN 17645. Digitale Plattform, kostenlos. Machen Sie mit.',
      keywords: ['Pool-Initiative', 'Pool-Dokumentation', 'Pool-Sektor', 'Pool-Fachleute']
    },
    it: {
      title: 'PoolLex Initiative · Classificazione Sostenibilità Piscine',
      description: "Iniziativa aperta per democratizzare la classificazione ambientale delle piscine secondo UNI EN 17645. Piattaforma digitale, senza costo. Unisciti.",
      keywords: ['iniziativa piscine', 'documentazione piscine', 'settore piscine', 'professionisti piscine']
    },
    nl: {
      title: 'PoolLex Initiative · Duurzaamheidsclassificatie Zwembaden',
      description: 'Europees initiatief voor de classificatie van de milieuduurzaamheid van zwembaden volgens NEN-EN 17645. Drie niveaus: Fiche, Keurmerk, Installateurs.',
      keywords: ['PoolLex Initiative', 'NEN-EN 17645', 'duurzaamheidscertificering zwembad', 'Europese zwembadnorm', 'milieuklasse zwembad', 'zwembadkeurmerk']
    }
  },
  professionals: {
    es: {
      title: 'Documento de Entrega Profesional de Piscina GRATIS',
      description: 'Genera GRATIS el documento de entrega profesional de piscina para instaladores y mantenedores. Alta gratuita. Documenta cada trabajo que entregas.',
      keywords: ['documento entrega piscina gratis', 'documento entrega profesional piscina', 'certificado entrega piscina instalador', 'documentación piscina mantenimiento', 'instalador piscinas documento', 'mantenedor piscinas certificado']
    },
    en: {
      title: 'FREE Professional Pool Delivery Document | PoolLex.io',
      description: 'Generate FREE professional pool delivery documents for installers and maintenance companies. Free sign-up. Document every job you hand over.',
      keywords: ['free pool delivery document', 'professional pool certificate', 'pool installer documentation', 'pool maintenance certificate', 'pool technician documents']
    },
    fr: {
      title: 'Document de Livraison Piscine Professionnel GRATUIT',
      description: 'Générez GRATUITEMENT le document de livraison professionnel de piscine pour installateurs et mainteneurs. Inscription gratuite.',
      keywords: ['document livraison piscine gratuit', 'certificat piscine installateur', 'documentation piscine maintenance', 'document professionnel piscine']
    },
    de: {
      title: 'KOSTENLOSES Professionelles Pool-Übergabedokument',
      description: 'Erstellen Sie KOSTENLOS professionelle Pool-Übergabedokumente für Installateure und Wartungsunternehmen. Kostenlose Anmeldung.',
      keywords: ['Pool-Übergabedokument kostenlos', 'Pool-Zertifikat Installateur', 'Pool-Wartung Dokumentation', 'professionelles Pool-Dokument']
    },
    it: {
      title: 'Documento di Consegna Piscina Professionale GRATIS',
      description: 'Genera GRATIS il documento di consegna professionale per installatori e manutentori di piscine. Iscrizione gratuita. Documenta ogni lavoro.',
      keywords: ['documento consegna piscina gratis', 'certificato piscina installatore', 'documentazione piscina manutenzione', 'documento professionale piscina']
    },
    nl: {
      title: 'Professioneel Opleverdocument Zwembad GRATIS | PoolLex.io',
      description: 'Genereer gratis professionele opleverdocumentatie voor je zwembadklanten: technische gegevens, milieuklasse NEN-EN 17645 en PoolLex ID. Gratis account.',
      keywords: ['opleverdocument zwembad', 'zwembad oplevering klant', 'professionele zwembaddocumentatie', 'zwembadrapport', 'PoolLex professionals']
    }
  },
  // Sprint 119.74 — SEO installers reorientat a EN 17645 sostenibilitat
  // (Sprint 119.27 va deixar stand-by "Delivery Document"; ara vehicle
  // B2B és "Informe de Mejoras EN 17645" via Sprint 119.4/5).
  installers: {
    es: {
      title: 'Instaladores: Informes de Mejoras EN 17645 para Piscinas',
      description: 'Instaladores: alta gratuita al programa PoolLex.io. Genera informes de mejoras EN 17645 para cada cliente, con clasificación ambiental estimada.',
      keywords: ['instaladores piscinas registro', 'informe mejoras piscina EN 17645', 'certificado sostenibilidad piscina instalador', 'clasificación ambiental piscina', 'programa instaladores piscina sostenible', 'PoolLex instaladores']
    },
    en: {
      title: 'Pool Installers: Generate EN 17645 Improvement Reports',
      description: 'Pool installers: free registration to the PoolLex.io programme. Generate EN 17645 improvement reports for each client, with an estimated class.',
      keywords: ['pool installer registration', 'EN 17645 pool improvement report', 'pool sustainability certificate installer', 'pool environmental classification', 'sustainable pool installer programme']
    },
    fr: {
      title: 'Installateurs: Rapports d\'Amélioration NF EN 17645',
      description: 'Installateurs: inscription gratuite au programme PoolLex.io. Générez des rapports d\'amélioration NF EN 17645 pour chaque client.',
      keywords: ['inscription installateur piscine', 'rapport amélioration NF EN 17645 piscine', 'certificat durabilité piscine installateur', 'classification environnementale piscine', 'programme installateur piscine durable']
    },
    de: {
      title: 'Pool-Installateure: DIN EN 17645 Verbesserungsberichte',
      description: 'Pool-Installateure: kostenlose Anmeldung zum PoolLex.io-Programm. Erstellen Sie DIN EN 17645 Verbesserungsberichte für jeden Kunden.',
      keywords: ['Pool-Installateur Anmeldung', 'DIN EN 17645 Pool Verbesserungsbericht', 'Pool Nachhaltigkeitszertifikat Installateur', 'Pool Umweltklassifizierung']
    },
    it: {
      title: 'Installatori: Rapporti di Miglioramento UNI EN 17645',
      description: 'Installatori: registrazione gratuita al programma PoolLex.io. Genera rapporti di miglioramento UNI EN 17645 per ogni cliente.',
      keywords: ['registrazione installatore piscine', 'rapporto miglioramento UNI EN 17645 piscina', 'certificato sostenibilità piscina installatore', 'classificazione ambientale piscina']
    },
    nl: {
      title: 'Zwembadinstallateurs: NEN-EN 17645 Verbeterrapporten',
      description: 'Voor zwembadinstallateurs: maak verbeterrapporten volgens NEN-EN 17645 voor je klanten. Toon de milieuwinst van elke ingreep met cijfers.',
      keywords: ['zwembadinstallateur', 'verbeterrapport zwembad', 'NEN-EN 17645 installateur', 'zwembad renovatie duurzaamheid', 'installateur zwembadcertificering']
    }
  },
  // New SEO config for maintenance page
  maintenance: {
    es: {
      title: 'Documentación Profesional para Mantenedores de Piscinas',
      description: 'Mantenedores: documenta GRATIS cada intervención con fichas técnicas profesionales. Historial de mantenimiento, registros y certificados para tus clientes.',
      keywords: ['documento mantenimiento piscina gratis', 'ficha técnica mantenedor piscinas', 'registro mantenimiento piscina', 'certificado mantenimiento piscina profesional']
    },
    en: {
      title: 'FREE Professional Documentation for Pool Maintenance',
      description: 'Maintenance companies: document FREE each intervention with professional technical sheets. Maintenance history, records and certificates for your clients.',
      keywords: ['free pool maintenance document', 'pool maintenance certificate', 'pool service record', 'professional pool maintenance documentation']
    },
    fr: {
      title: 'Documentation Professionnelle pour Mainteneurs de Piscines',
      description: 'Mainteneurs: documentez GRATUITEMENT chaque intervention avec fiches techniques professionnelles. Historique, registres et certificats pour vos clients.',
      keywords: ['document maintenance piscine gratuit', 'certificat maintenance piscine', 'registre entretien piscine professionnel']
    },
    de: {
      title: 'KOSTENLOSE Professionelle Dokumentation für Pool-Wartung',
      description: 'Wartungsunternehmen: dokumentieren Sie KOSTENLOS jeden Einsatz mit professionellen technischen Datenblättern. Wartungshistorie und Zertifikate.',
      keywords: ['Pool-Wartungsdokument kostenlos', 'Pool-Wartungszertifikat', 'professionelle Pool-Wartungsdokumentation']
    },
    it: {
      title: 'Documentazione Professionale per Manutentori Piscine',
      description: 'Manutentori: documenta GRATIS ogni intervento con schede tecniche professionali. Storico manutenzione, registri e certificati per i tuoi clienti.',
      keywords: ['documento manutenzione piscina gratis', 'certificato manutenzione piscina', 'registro manutenzione piscina professionale']
    },
    nl: {
      title: 'Professionele Documentatie voor Zwembadonderhoud',
      description: 'Voor onderhoudsbedrijven: documenteer de zwembaden die je beheert met technische fiches, verbruiksgegevens en milieuklasse NEN-EN 17645.',
      keywords: ['zwembadonderhoud documentatie', 'onderhoudsbedrijf zwembad', 'zwembadbeheer', 'onderhoudsrapport zwembad', 'zwembad servicebedrijf']
    }
  }
};

// Structured data generators
// Sprint 119.74 — Organization schema enriquit amb sameAs (LinkedIn
// fundador) + founder + areaServed. Reforça senyal d'autoritat/marca
// a Google i Knowledge Graph. `sameAs` és el camp clau: connecta el
// domini amb perfils oficials verificats.
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "PoolLex.io",
  "alternateName": "PoolLex",
  "url": "https://poollex.io",
  "logo": "https://poollex.io/poollex-logo-full.png",
  "description": "Plataforma digital para la clasificación ambiental de piscinas basada en la norma europea UNE-EN 17645:2023.",
  "founder": {
    "@type": "Person",
    "name": "Víctor Saiz Vela",
    "jobTitle": "Ingeniero Industrial Colegiado",
    "url": "https://www.linkedin.com/in/victorsaizvela/"
  },
  "areaServed": [
    { "@type": "Country", "name": "Spain" },
    { "@type": "Country", "name": "France" },
    { "@type": "Country", "name": "Italy" },
    { "@type": "Country", "name": "Germany" }
  ],
  "knowsAbout": [
    "UNE-EN 17645:2023",
    "Pool sustainability certification",
    "Environmental pool assessment",
    "Pool energy efficiency"
  ],
  "sameAs": [
    "https://www.linkedin.com/in/victorsaizvela/"
  ]
});

export const generateWebApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "PoolLex ID Generator",
  "url": "https://poollex.io/ficha-tecnica",
  "applicationCategory": "UtilityApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR"
  },
  "description": "Generador gratuito de PoolLex IDs técnicos para piscinas"
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://poollex.io${item.path}`
  }))
});

// Sprint SEO Blog v1 (2026-08-01) — Article schema per posts de blog.
// S'usa combinat amb generateFAQSchema en un @graph al SEO structuredData
// per aconseguir rich snippets doble (article card + FAQ dropdown).
export const generateArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.meta_description,
  "author": {
    "@type": "Person",
    "name": "Víctor Saiz Vela",
    "url": "https://www.linkedin.com/in/victorsaizvela/"
  },
  "publisher": {
    "@type": "Organization",
    "name": "PoolLex.io",
    "logo": {
      "@type": "ImageObject",
      "url": "https://poollex.io/poollex-logo-full.png"
    }
  },
  "datePublished": article.publish_date,
  "dateModified": article.publish_date,
  "mainEntityOfPage": `https://poollex.io/blog/${article.slug}`,
  "inLanguage": "es-ES",
  ...(article.keywords && article.keywords.length > 0
    ? { "keywords": article.keywords.join(", ") }
    : {})
});

export const generateHowToSchema = (title, description, steps) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": title,
  "description": description,
  "step": steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

export default SEO;
